(ns ncr.nsql
  (:require
   [clojure.string :as str]
   [clojure.set :as set]
   [clojure.core.match :refer [match]]
   #?(:clj
      [instaparse.core :refer [defparser] :as parse]
      :cljs
      [instaparse.core :refer-macros [defparser] :as parse])
   [instaparse.combinators :as c]

   [ncr.common.util :as u]

   [ncr.nsql.context :as context]
   [ncr.nsql.locale :as locale]
   [ncr.nsql.simple :as simple]))

; [:immediate :string "aaa"]
; [:property :user :reference/owner]
; [:call :+ ...]

(defparser parser (u/inline-resource "ncr/nsql/grammar.bnf"))

(defn- make-fakes [tag s]
  (mapv (fn [n] {:id (str (gensym (name tag))) :name n}) (str/split s #"\W+")))

(def ^:private labels
  (make-fakes :label "Lorem ipsum dolor sit amet, consectetur adipiscing elit"))

(def ^:private folders
  (make-fakes :folder "sed do eiusmod tempor incididunt ut labore"))

(def ^:private users
  (make-fakes :user "quis nostrud exercitation ullamco laboris nisi ut aliquip"))

(defn- ident->keyword [[tag & args]]
  {:pre (= tag :identifier)}
  (apply keyword args))

(defn- cmp->keyword [op]
  (-> op str/lower-case (str/replace #"\s+" "-") keyword))

#?(:clj
   (do
     (defn- parse-int [s] (Long/parseLong s))
     (defn- parse-decimal [s] (BigDecimal. s)))

   :cljs
   (do
     (defn- parse-int [s] (js/parseInt s))
     (defn- parse-decimal [s] (js/parseFloat s))))

(defn- ip->ir [form]
  {:pre (vector? form)}

  (let [[op & tail] form]
    (with-meta
      (case op
        :string [:immediate op (first tail)]
        :integer [:immediate op (parse-int (first tail))]
        :decimal [:immediate op (parse-decimal (first tail))]

        :struct-date (let [s (subs (first tail) 1)]
                       [:immediate :date (locale/parse-date :date s)])

        :simple-funcall (simple/funcall->ir form)

        :struct-deref
        (let [kw (ident->keyword (first tail))]
          (or (context/lookup-ident kw)
            [:error {:code :unknown-identifier :details {:id kw}}]))

        :funcall
        (into [:call (ident->keyword (first tail))]
          (map ip->ir (rest tail)))

        :cmp
        (let [[arg1 cmp arg2] tail]
          [:call (cmp->keyword cmp) (ip->ir arg1) (ip->ir arg2)])

        (into [:call op] (map ip->ir tail)))
      (meta form))))

(defn- ip-meta->loc
  [{:keys [:instaparse.gll/start-index :instaparse.gll/end-index] :as m}]
  (when m
    {:loc [start-index end-index]}))

(defn- generate* [emit expr]
  (let [result
        (match expr
          [:call op & args]
          (let [args (mapv #(generate* emit %) args)
                errors (mapcat #(match % [:error & es] es _ nil) args)]
            (if (seq errors)
              (into [:error] errors)
              (emit (into [:call op] args))))

          [:error _]
          expr

          _
          (emit expr))]

    (match result
      [:error & errors]
      (if-let [l (ip-meta->loc (meta expr))]
        (into [:error] (mapv #(merge l %) errors))
        result)

      _
      result)))

(defn generate [emit expr]
  (let [r (generate* emit expr)]
    (match r
      [:error & errors]
      [:error (vec errors)]

      _
      r)))

(defn generate-criterion [emit expr]
  (match (generate emit expr)
    [:error reason]
    [:error reason]

    [:boolean expr']
    expr'

    [type _]
    [:error [:nsql-invalid-type type]]))

(defn- unescape [s]
  (str/replace s #"\\(.)" "$1"))

#_(unescape "asd")
#_(unescape "as\\\\d\\\"\"")

(defn- unescape-strings [tree]
  (match tree
    [op & args]
    (let [f (if (#{:string :simple-args} op) unescape unescape-strings)]
      (with-meta (into [op] (map f args)) (meta tree)))

    any
    any))

#_(unescape-strings
    [:and
     [:and
      [:simple-funcall
       [:simple-not "-"]
       [:simple-noun-pred
        [:identifier "common" "date"]
        [:identifier "before"]]
       [:simple-args "2011" "12\\\"3"]]
      [:cmp
       [:struct-deref [:identifier "attachments"]]
       "is not"
       [:struct-deref [:identifier "null"]]]]
     [:funcall [:identifier "in_body"] [:string "as\\\" d"]]])

(defn monadic-parse [s start]
  (let [p (parser s :start start)]
    (if-let [{:keys [index line column]} (parse/get-failure p)]
      [:error
       {:code :parse-error
        :loc [index]}]
      (with-meta (map unescape-strings p) (meta p)))))

(defn parse-struct [s]
  (u/error-m [parsed (monadic-parse s :struct-expr)]
    (ip->ir (first parsed))))

(defn parse-simple [s]
  (when-not (empty? s)
    (u/error-m [parsed (monadic-parse s :simple-query)]
      (let [{:keys [simple-word simple-funcall]}
            (group-by first parsed)

            words
            (map peek simple-word)

            clauses
            (cond-> (mapv ip->ir simple-funcall)
              (seq words)
              (conj [:call
                     :fts
                     (context/lookup-ident :text)
                     [:immediate :string (str/join " " words)]]))]
        (when (seq clauses)
          (reduce (fn [s x] [:call :and s x]) clauses))))))

(defn- prewalk [f ir]
  (match ir
    [:call op & args]
    (apply merge-with merge
      (f ir)
      (for [arg args] (prewalk f arg)))

    _
    (f ir)))

(defn- analyzer [ir]
  (match ir
    [:call :fts [:immediate :string s]]
    {:ops {:fts true} :flags {:fts s}}

    [:call op & args]
    {:ops {op true}}

    [:column ctype cname]
    {:columns {cname true}}

    [:error & _]
    {:flags {:errors true}}

    _
    nil))

(defn analyze [ir]
  (let [just
        (match ir
          [:call :=
           [:column :folder :reference/folder]
           [:immediate :string s]]
          {:flags {:folder s}}

          [:call :=
           [:column :labels :reference/labels]
           [:immediate :string s]]
          {:flags {:label s}}

          _
          nil)]
    (merge just (prewalk analyzer ir))))

(defn maybe-quote [s]
  (let [s (str/escape s {\" "\\\"" \\ "\\\\"})]
    (if (some? (str/last-index-of s \space))
      (str \" s \")
      s)))

(defn- simple-clause->string [c]
  (match c
    [:simple-word w] w
    [:simple-funcall & tail]
    (let [{:keys [simple-not simple-noun-pred simple-args]}
          (into {} (map (juxt first rest) tail))]
      (str
        (first simple-not)
        (str/join "/" (map second simple-noun-pred))
        ":"
        (str/join ":" (map maybe-quote simple-args))))))

(defn- unparse-simple [sq]
  (str/join " " (map simple-clause->string sq)))

#_(unparse-simple (monadic-parse "asd -label:\"xxx\\\" qwe\"" :simple-query))

#_(parse-simple "label:'aa\" aa'")

#_(-> "label:\"xxx \\\"zzz\""
    (monadic-parse :simple-query)
    unparse-simple)

(defn analyze-simple [q]
  (reduce
    #(match %2
       [:simple-funcall
        [:simple-noun-pred [:identifier ident]]
        [:simple-args arg]]
       (let [kw (keyword ident)]
         (if (= kw :label)
           (update %1 kw (fnil conj []) arg)
           (assoc %1 kw arg)))

       _
       %1)
    {}
    (monadic-parse q :simple-query)))

#_(analyze-simple "qqq label:zzz wwww label:qqq in:Inbox date:2022/03")

(defn- intersect-simple [a b]
  (let [a (vec (monadic-parse a :simple-query))
        b (set (monadic-parse b :simple-query))
        {found true not-found false} (group-by #(contains? b %) a)
        found (set found)
        unused (set/difference b found)]
    {:a a :b b :found found :not-found not-found :unused unused}))

#_(intersect-simple "qwe asd label:zzz label:qqq zxc" "label:zzz in:Inbox")

(defn simple-add [a b]
  (let [{:keys [a unused]} (intersect-simple a b)]
    (unparse-simple (into a unused))))

#_(simple-add "asd zxc label:qqq" "label:qqq label:zzz")

(defn simple-delete [a b]
  (unparse-simple (:not-found (intersect-simple a b))))

#_(simple-delete "asd zxc label:qqq" "label:qqq label:zzz")

(defn simple-toggle [a b]
  (let [{:keys [not-found unused]} (intersect-simple a b)]
    (unparse-simple (into not-found unused))))

#_(simple-toggle "asd zxc label:qqq" "label:qqq\\\" label:zzz")

(comment
  (do
    (require '[honey.sql :as sql])
    (require '[ncr.nsql.backend.hsql :as b.hsql])

    (defn- struct->hsql
      ([s] (struct->hsql s {}))

      ([s opts]
       (context/with-context opts
         (generate b.hsql/emit (parse-struct s)))))

    (defn- simple->hsql
      ([s] (simple->hsql s {}))
      ([s opts]
       (context/with-context opts
         (generate b.hsql/emit (parse-simple s))))))

  (parser "aaa.bbb.ccc"
    :start :struct-expr)

  (context/with-context {:enums {:folders folders :users users} :locale :de}
    (parse-simple "date:2022/01/15"))

  (context/with-context {:enums {:folders folders :users users} :locale :de}
    (analyze
      (parse-simple "date:aaa date:2022/01/15")))

  (parse-simple "aaa bbb label:qqq label:zzz")
  (parse-simple "text:aaaa")

  (sql/format
    {:select :*
     :from :some
     :where
     (second
       (simple->hsql "провер date:2021/{0[79],1[12]} car"
         {:enums {:folders folders :users users} :locale :de}))})

  (sql/format
    {:select :*
     :from :some
     :where
     (second
       (simple->hsql "word1 -common.date/before:a011 word2 value:x owner:n*"
         {:enums {:folders folders :users users} :locale :de}))})

  (monadic-parse
    "quick brown folder:{fox*,jump*} over label:lazy dog"
    :simple-query)

  (simple->hsql "label:{aaa*,bbb}"
    {:enums {:labels labels :folders folders :users users} :locale :de})

  (sql/format
    {:select :*
     :from :some
     :where
     (second
       (simple->hsql "value:100"
         {:enums {:labels labels :folders folders :users users} :locale :de}))})

  (sql/format
    {:select :*
     :from :some
     :where
     (second
       (struct->hsql
         "-common.date/before:2011 and owner:n* and attachments is not null"
         {:enums {:folders folders :users users} :locale :de}))})

  (monadic-parse
    "-common.date/before:2011:123 and attachments is not null and in_body(\"asd\")"
    :struct-expr)

  (parse-struct
    "-common.date/before:2011 and attachments is not null and in_body(\"asd\")")

  (struct->hsql
    "aaaa and body"
    {:enums {:folders folders :users users} :locale :de})

  (struct->hsql
    "fts(all_texts, \"aaaa bbb\")"
    {:enums {:folders folders :users users} :locale :de})

  (sql/format
    {:select :*
     :from :some
     :where
     (second
       (struct->hsql
         "date < #2022-04-05 and owner:n* and status:inbox or value=123"
         {:enums {:folders folders :users users} :locale :de}))})

  (parser "1 * 2 or " :start :struct-expr))
